import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import Icon from '../AppIcon';
import { isMobile } from 'react-device-detect';

const Dialog = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  onConfirm, 
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  showCancel = true,
  showConfirm = true,
  isLoading = false
}) => {
  const dialogRef = useRef(null);
  const controls = useAnimation();
  const isMobileView = window.innerWidth < 768;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      controls.start('visible');
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
      controls.start('hidden');
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen, controls]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const variants = {
    initial: { opacity: 0, y: 20, scale: 0.98 },
    animate: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: 'spring',
        damping: 25,
        stiffness: 300,
        mass: 0.5
      }
    },
    exit: { 
      opacity: 0, 
      y: 20, 
      scale: 0.98,
      transition: { 
        duration: 0.2 
      }
    },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const handleOverlayClick = (e) => {
    // Ne pas fermer si on clique sur la popup elle-même
    if (!dialogRef.current || dialogRef.current.contains(e.target)) {
      return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
          onClick={handleOverlayClick}
        >
          {/* Overlay with better touch handling */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            variants={overlayVariants}
            initial="hidden"
            animate={isOpen ? "visible" : "hidden"}
            exit="exit"
          />

          {/* Main container with better mobile handling */}
          <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
            <motion.div
              ref={dialogRef}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              className={`relative w-full max-w-3xl bg-surface rounded-lg shadow-xl border border-[#00ffc3] max-h-[90vh] flex flex-col ${isMobileView ? 'w-[95%]' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with improved mobile styling */}
              <div className="flex-shrink-0 px-4 sm:px-6 py-3 flex justify-between items-center border-b border-street sticky top-0 bg-surface z-10">
                <h3 className="text-lg sm:text-xl font-bold text-foreground truncate pr-2" id="modal-title">
                  {title}
                </h3>
                <button
                  type="button"
                  className="p-1 sm:p-2 -mr-1 sm:-mr-2 text-muted-foreground hover:text-foreground focus:outline-none rounded-full hover:bg-surface transition-colors"
                  onClick={onClose}
                  aria-label="Fermer"
                >
                  <Icon name="X" size={isMobileView ? 20 : 24} />
                </button>
              </div>

              {/* Content with better scrolling */}
              <div className="overflow-y-auto flex-grow px-4 sm:px-6 py-4 -mx-1 px-1">
                <div className="min-h-[1px]">
                  {children}
                </div>
              </div>

              {/* Footer with buttons */}
              {(showConfirm || showCancel) && (
                <div className="flex-shrink-0 bg-surface/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-street">
                  {showConfirm && (
                    <button
                      type="button"
                      className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent text-base font-medium text-accent-foreground hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:ml-3 sm:w-auto sm:text-sm transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                      onClick={(e) => {
                        console.log('Bouton de confirmation cliqué');
                        e.stopPropagation();
                        if (!isLoading && onConfirm) {
                          console.log('Appel de onConfirm');
                          onConfirm();
                        } else {
                          console.log('Bouton désactivé ou onConfirm non défini', { isLoading, hasOnConfirm: !!onConfirm });
                        }
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {confirmText}
                        </>
                      ) : (
                        confirmText
                      )}
                    </button>
                  )}
                  {showCancel && (
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-muted-foreground/20 shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      {cancelText}
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Dialog;